import React, { useEffect, useMemo, useState } from 'react';
import { Col, DatePicker, Divider, Input, Row, Select } from 'antd';
import { injectIntl } from 'react-intl';

import {
  Form,
  FormCancelButton,
  FormSaveButton,
  UploadFile,
} from 'common/components';
import {
  useGetCertificateTypes,
  useGetMemberCertificatesLocations
} from './queries';

import { WrapperSelect } from 'common/components';
import { StyledModal } from 'common/components';
import {
  DATE_FORMAT,
  UPLOAD_MAX_SIZE,
} from 'static/Constants';

import * as endpoints from 'services/members/endpoints';

import { DOCUMENT_CERTIFICATE_LIMIT_CHARACTER } from 'pages/qa-spec/constant';
import { DEFAULT_STATE } from './CertificateTab';
import { exportFormDateWithoutTime, formatMDY } from 'utils/formatDate';
import moment from 'moment';
import { CERTIFICATE_STATUS, SUPPORT_TYPES } from './constants';
import InfoModal from './InfoModal';
import { useParams } from 'react-router-dom/cjs/react-router-dom';

const { Option } = Select;

const CreateCertificateModal = injectIntl((props) => {
  const { onSubmit, setActionState, actionState, archivedCertificateIds = [] } = props;
  const { data, isLoading, isError } = useGetCertificateTypes();
  const [assetId, setAssetId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [formInstance] = Form.useForm();
  const [modalRowData, setModalRowData] = useState(null);
  const { id } = useParams();

  const memberIdValue = id ? id : null;

  const { data: certificateLocations = [], refetch: refetchCertificateLocations } = useGetMemberCertificatesLocations({ memberId: memberIdValue });

  const filteredCertificateLocations = useMemo(() => {
    return (certificateLocations || []).filter(
      (c) => !archivedCertificateIds.includes(c.id)
    );
  }, [certificateLocations, archivedCertificateIds]);

  const handleSubmit = async () => {
    const values = await formInstance.validateFields();
    const type = values.type;

    const certificateByType = filteredCertificateLocations.filter(c => c.certificateType == type);
    setModalRowData(certificateByType);

    if (certificateByType.length > 0) {
      setShowInfoModal(true);
      return;
    }

    // const values = await formInstance?.validateFields();
    setActionState(DEFAULT_STATE);
    onSubmit &&
      onSubmit({
        ...values,
        tempFileName: assetId,
        expirationDate: exportFormDateWithoutTime(values?.expirationDate),
      });
  };

  const handleInfoSubmit = async (params) => {
    setShowInfoModal(false);

    const values = await formInstance?.validateFields();
    setActionState(DEFAULT_STATE);
    onSubmit &&
      onSubmit({
        ...values,
        ...params,
        tempFileName: assetId,
        expirationDate: exportFormDateWithoutTime(values?.expirationDate),
      });
  };

  useEffect(() => {
    if (actionState.mode === 'edit' && actionState.data) {
      let date = null;

      if (actionState.data?.expirationDate) {
        // if your API returns ISO format, this is enough:
        date = moment(formatMDY(actionState.data.expirationDate));
      }

      formInstance.setFieldsValue({
        ...actionState.data,
        expirationDate: date,
      });
    } else if (actionState.mode === 'add') {
      formInstance.resetFields();
      setAssetId(null);
    }
  }, [actionState.mode, actionState.data, formInstance]);

  useEffect(() => {
    if (actionState.visible) {
      // Fetch fresh data when the modal is opened
      refetchCertificateLocations();
    }
  }, [actionState.visible]);

  const title =
    actionState.mode === 'add' ? 'Add Certificate' : 'Edit Certificate';

  const onFileChange = () => {
    setAssetId(null);
  };

  const handleUploadFile = (response) => {
    if (!response?.data) return;

    const {
      data: { tempFileName },
    } = response.data;

    if (tempFileName) {
      setAssetId(tempFileName);
    }
    return;
  };

  const height = actionState.mode === 'add' ? 550 : 200;

  // Helper function to disable dates before tomorrow
  const disabledDate = (current) => {
    // Can only select dates after today 
    return current && current < moment().startOf('day');
  };

  return (
    <React.Fragment>
      <StyledModal
        title={title}
        width={800}
        destroyOnClose
        maskClosable={false}
        visible={actionState.visible}
        onCancel={() => setActionState(DEFAULT_STATE)}
        bodyStyle={{ height }}
        onOk={handleSubmit}
        footer={[
          <FormCancelButton onClick={() => setActionState(DEFAULT_STATE)} />,
          <FormSaveButton
            text='Upload'
            style={{ marginLeft: 10 }}
            onClick={handleSubmit}
            disabled={!assetId && actionState.mode === 'add'}
          />,
        ]}
      >
        <Form
          form={formInstance}
          className='company-certificate-form'
          layout='vertical'
          id='company-certificate-edit-form'
          initialValues={{
            level: 'Facility',
            status: 'Active',
          }}
        >
          {actionState.mode === 'add' && (
            <>
              <div style={{ height: 300, marginTop: 12 }}>
                <UploadFile
                  multiple={false}
                  multipleImport={false}
                  supportTypes={SUPPORT_TYPES}
                  showList
                  showPreview
                  showMultiple={false}
                  apiUrl={endpoints.UPLOAD_MEMBER_CERTIFICATE}
                  maxSize={UPLOAD_MAX_SIZE.GENERAL}
                  getResponse={handleUploadFile}
                  onFileChange={onFileChange}
                  forceDisableManudalUpload={assetId}
                />
              </div>
              <Divider />
            </>
          )}

          <Row gutter={[8, 8]}>
            <Col span={8}>
              <Form.Item
                name='level'
                label='Certificate Level'
                rules={[
                  {
                    required: true,
                    message: 'Please select Certificate Type!',
                  },
                ]}
              >
                <WrapperSelect
                  getPopupContainer={() =>
                    document.getElementById('company-certificate-edit-form')
                  }
                  disabled
                >
                  <Option value={'Facility '}>Facility</Option>
                </WrapperSelect>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='certificateName'
                label='Certificate Name'
                rules={[
                  {
                    required: true,
                    message: 'Please input Certificate Name!',
                  },
                ]}
              >
                <Input
                  name='email'
                  placeholder='Please input Certificate Name'
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='type'
                label='Certificate Type'
                rules={[
                  {
                    required: true,
                    message: 'Please select Certificate Type!',
                  },
                ]}
              >
                <WrapperSelect
                  showSearch
                  placeholder="Select a certificate type"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  loading={isLoading}
                  allowClear
                >
                  {data?.map((item) => (
                    <Select.Option key={item} value={item}>
                      {item}
                    </Select.Option>
                  ))}
                </WrapperSelect>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            <Col span={8}>
              <Form.Item
                label='Certificate Body'
                name='certificateBody'
                rules={[
                  {
                    required: true,
                    message: 'Please input Certificate Body!',
                  },
                ]}
              >
                <Input
                  showCount
                  maxLength={
                    DOCUMENT_CERTIFICATE_LIMIT_CHARACTER.certificateBody
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='status'
                label='Status'
                rules={[
                  {
                    required: true,
                    message: 'Please select Status!',
                  },
                ]}
              >
                <WrapperSelect
                  getPopupContainer={() =>
                    document.getElementById('company-certificate-edit-form')
                  }
                >
                  {[
                    CERTIFICATE_STATUS.map((type, index) => {
                      return (
                        <Option value={type?.value} key={index}>
                          {type?.displayName}
                        </Option>
                      );
                    }),
                  ]}
                </WrapperSelect>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label='Certificate Expiry Date'
                name='expirationDate'
                rules={[
                  {
                    required: true,
                    message: 'Please select certificate expiry date!',
                  },
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format={DATE_FORMAT.SYSTEM_FORMAT}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </StyledModal>

      {showInfoModal && (
        <InfoModal
          actionState={actionState}
          onInfoSubmit={handleInfoSubmit}
          setActionState={setActionState}
          rowData={modalRowData}
          setShowInfoModal={setShowInfoModal}
        />
      )}
    </React.Fragment>
  );
});

export default CreateCertificateModal;
